import { useState, useEffect, useRef, useCallback } from 'react';

const SobreHero = () => {
  const [stage, setStage] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 200),
      setTimeout(() => setStage(2), 500),
      setTimeout(() => setStage(3), 900),
      setTimeout(() => setStage(4), 1300),
      setTimeout(() => setStage(5), 1700),
      setTimeout(() => setStage(6), 2100),
      setTimeout(() => setStage(7), 2500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Parallax scroll handler
  const handleScroll = useCallback(() => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const sectionHeight = heroRef.current.offsetHeight;
    // Only apply parallax when hero is in viewport
    if (rect.bottom > 0 && rect.top < window.innerHeight) {
      setScrollY(-rect.top * 0.35);
    }
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  return (
    <section ref={heroRef} className="relative min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/32f3bc36bea2a3e696d7bc6c9c1538c4.jpeg"
          alt="Bia Holmes - Fundadora do Carta do Eu Futuro"
          className="w-full h-[120%] object-cover object-top will-change-transform"
          style={{
            transform: `translateY(${scrollY}px) scale(1.05)`,
            transition: 'transform 0.1s linear',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute top-28 right-32 w-2 h-2 bg-gold-500/50 rounded-full animate-float"></div>
      <div
        className="absolute top-52 right-64 w-1.5 h-1.5 bg-gold-400/30 rounded-full animate-float"
        style={{ animationDelay: '1.5s' }}
      ></div>
      <div
        className="absolute bottom-36 right-48 w-1 h-1 bg-gold-300/40 rounded-full animate-float"
        style={{ animationDelay: '2.5s' }}
      ></div>

      <div className="container mx-auto px-6 lg:px-16 py-20 relative z-10">
        <div className="max-w-3xl">
          {/* Badge - slide from left */}
          <div
            className="flex items-center gap-4 mb-8 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 1 ? 1 : 0,
              transform: stage >= 1 ? 'translateX(0)' : 'translateX(-40px)',
            }}
          >
            <div className="w-12 h-px bg-gradient-to-r from-gold-500 to-gold-300"></div>
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400">
              Quem sou eu
            </span>
          </div>

          {/* Title lines - staggered reveal */}
          <h1 className="font-['Playfair_Display'] text-5xl lg:text-7xl leading-[1.1] mb-8 text-white">
            <span
              className="font-normal block transition-all duration-700 ease-out"
              style={{
                opacity: stage >= 2 ? 1 : 0,
                transform: stage >= 2 ? 'translateY(0)' : 'translateY(30px)',
                filter: stage >= 2 ? 'blur(0px)' : 'blur(4px)',
              }}
            >
              Eu sou
            </span>
            <span
              className="italic font-light block text-gold-400 mt-3 transition-all duration-800 ease-out"
              style={{
                opacity: stage >= 3 ? 1 : 0,
                transform: stage >= 3 ? 'translateX(0) scale(1)' : 'translateX(-30px) scale(0.95)',
                filter: stage >= 3 ? 'blur(0px)' : 'blur(4px)',
              }}
            >
              Bia Holmes.
            </span>
            <span
              className="font-bold block mt-2 text-3xl lg:text-4xl transition-all duration-700 ease-out"
              style={{
                opacity: stage >= 3 ? 1 : 0,
                transform: stage >= 3 ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: '200ms',
              }}
            >
              E esta é a minha história.
            </span>
          </h1>

          {/* Paragraphs - fade up */}
          <p
            className="font-['Montserrat'] text-base lg:text-lg text-white/55 mb-6 leading-relaxed max-w-xl font-light tracking-wide transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 4 ? 1 : 0,
              transform: stage >= 4 ? 'translateY(0)' : 'translateY(25px)',
            }}
          >
            Sobrevivi a 30 anos de síndrome do pânico, ansiedade e traumas profundos. Passei por tudo o que você pode imaginar — e o que não pode também. Até que decidi reescrever minha própria história.
          </p>

          <p
            className="font-['Montserrat'] text-sm text-white/40 mb-10 leading-relaxed max-w-xl font-light tracking-wide transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 4 ? 1 : 0,
              transform: stage >= 4 ? 'translateY(0)' : 'translateY(25px)',
              transitionDelay: '150ms',
            }}
          >
            Hoje sou livre. E transformei cada técnica, cada ferramenta e cada descoberta que me salvou no{' '}
            <strong className="text-gold-400 font-medium">Carta do Eu Futuro</strong> — para que você
            não precise levar 30 anos para encontrar a sua liberdade.
          </p>

          {/* Avatar card - scale + fade */}
          <div
            className="flex items-center gap-4 mb-8 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 5 ? 1 : 0,
              transform: stage >= 5 ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.95)',
            }}
          >
            <div className="w-14 h-14 rounded-full border-2 border-gold-500/40 overflow-hidden">
              <img
                src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/32f3bc36bea2a3e696d7bc6c9c1538c4.jpeg"
                alt="Bia Holmes"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="font-['Playfair_Display'] text-lg text-white font-semibold">
                Bia Holmes
              </p>
              <p className="font-['Montserrat'] text-xs text-gold-400/70 tracking-wider">
                Fundadora &bull; Especialista em Transformação Pessoal
              </p>
            </div>
          </div>

          {/* Botão Press Kit */}
          <div
            className="transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 6 ? 1 : 0,
              transform: stage >= 6 ? 'translateY(0)' : 'translateY(15px)',
            }}
          >
            <a
              href="/imprensa"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-500/10 to-gold-600/5 hover:from-gold-500/20 hover:to-gold-600/10 border border-gold-500/30 hover:border-gold-500/50 text-gold-400 hover:text-gold-300 font-['Montserrat'] text-sm font-medium px-6 py-3 rounded-lg transition-all duration-300 shadow-[0_4px_20px_rgba(201,168,76,0.1)] hover:shadow-[0_6px_25px_rgba(201,168,76,0.2)] whitespace-nowrap cursor-pointer"
            >
              <i className="ri-newspaper-line text-base w-4 h-4 flex items-center justify-center" />
              Ver Press Kit Completo
              <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
            </a>
          </div>

          {/* Instagram - slide from left */}
          <div
            className="mt-6 flex items-center gap-3 transition-all duration-700 ease-out"
            style={{
              opacity: stage >= 7 ? 1 : 0,
              transform: stage >= 7 ? 'translateX(0)' : 'translateX(-20px)',
            }}
          >
            <a
              href="https://www.instagram.com/bia.holmes"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 font-['Montserrat'] text-xs text-gold-400/60 hover:text-gold-400 transition-colors duration-300 cursor-pointer tracking-wide"
            >
              <i className="ri-instagram-line text-sm"></i>
              @bia.holmes
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 transition-all duration-700 ease-out"
        style={{
          opacity: stage >= 7 ? 1 : 0,
          transform: stage >= 7 ? 'translateY(0)' : 'translateY(10px)',
        }}
      >
        <span className="font-['Montserrat'] text-[10px] text-gold-500/50 uppercase tracking-[0.3em]">
          Leia minha história
        </span>
        <div className="w-px h-8 bg-gradient-to-b from-gold-500/50 to-transparent animate-shimmer"></div>
      </div>
    </section>
  );
};

export default SobreHero;
